import { usePermissions } from "@/composables/usePermissions";
import { useAuthStore } from "@/stores/auth";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

export interface NavItem {
  name: string;
  path: string;
  icon: string;
  adminOnly?: boolean;
  requiredPermissions?: string[];
  deprecated?: boolean;
  children?: (NavItem | { type: "divider"; label: string })[];
}

export const useSidebarNavigation = () => {
  const authStore = useAuthStore();
  const route = useRoute();
  const router = useRouter();
  const { canAny } = usePermissions();

  // Admin menu expanded state
  const isAdminExpanded = ref(false);

  // Auto-expand admin menu when on admin route
  const isAdminRoute = computed(() => route.path.startsWith("/admin"));
  watch(
    isAdminRoute,
    (newVal) => {
      if (newVal) {
        isAdminExpanded.value = true;
      }
    },
    { immediate: true },
  );

  // Navigate without scrolling
  const navigateTo = (path: string, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    if (route.path !== path) {
      router.push(path);
    }
  };

  /**
   * Check if user has permission to see a menu item
   */
  const canSeeMenuItem = (item: NavItem): boolean => {
    // If no permissions required, show the item
    if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
      return true;
    }

    // Check if user has ANY of the required permissions
    return canAny(item.requiredPermissions).value;
  };

  /**
   * Filter menu items based on permissions
   */
  const filterMenuItems = (
    items: (NavItem | { type: "divider"; label: string })[],
  ): (NavItem | { type: "divider"; label: string })[] => {
    const filtered: (NavItem | { type: "divider"; label: string })[] = [];
    let lastWasDivider = true; // Start as true to remove leading dividers

    for (const item of items) {
      // Check if it's a divider
      if ("type" in item && item.type === "divider") {
        // Mark that we saw a divider, but don't add it yet
        lastWasDivider = true;
        continue;
      }

      // It's a menu item - check permissions
      if (canSeeMenuItem(item as NavItem)) {
        // If we had a divider before this item, add it now
        if (lastWasDivider && filtered.length > 0) {
          // Find the last divider we skipped
          const dividerIndex = items.indexOf(item) - 1;
          if (dividerIndex >= 0 && "type" in items[dividerIndex]) {
            filtered.push(items[dividerIndex]);
          }
        }
        filtered.push(item);
        lastWasDivider = false;
      }
    }

    return filtered;
  };

  const navItems = computed<NavItem[]>(() => {
    const items: NavItem[] = [
      {
        name: "Dashboard",
        path: "/user-dashboard",
        icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
      },
      {
        name: "Jobs",
        path: "/jobs",
        icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
      },
      {
        name: "Results",
        path: "/results",
        icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
      },
      {
        name: "Favorites",
        path: "/favorites",
        icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
      },
      {
        name: "Preisbewegungen",
        path: "/price-movers",
        icon: "M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z",
      },
      {
        name: "Batch Lists",
        path: "/batch-lists",
        icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
      },
      {
        name: "Batch Import",
        path: "/batch-import",
        icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12",
      },
      {
        name: "Shares",
        path: "/shares",
        icon: "M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z",
      },
      {
        name: "Exporte",
        path: "/exports",
        icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4",
      },
      {
        name: "Settings",
        path: "/user-settings",
        icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
        requiredPermissions: ["userSettings.viewOwn"],
      },
      {
        name: "Notifications",
        path: "/notifications",
        icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
      },
      {
        name: "Admin",
        path: "/admin",
        icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
        adminOnly: true,
        children: [
          { type: "divider", label: "User/Roles" },
          {
            name: "User Management",
            path: "/admin/users",
            icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
            requiredPermissions: ["roles.editAll"], // User Management braucht roles.editAll für fetchRoles()
          },
          {
            name: "Roles & Permissions",
            path: "/admin/roles",
            icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
            requiredPermissions: ["roles.editAll"], // Backend API braucht roles.viewAll, aber Admin hat nur editAll
          },
          { type: "divider", label: "Mappings" },
          {
            name: "Brands & Models",
            path: "/admin/mappings",
            icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
            requiredPermissions: ["mappings.editAll"],
          },
          {
            name: "Variants",
            path: "/admin/variants",
            icon: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z",
            requiredPermissions: ["variants.editAll"], // Admin hat editAll, nicht viewAll
          },
          { type: "divider", label: "Scraping" },
          {
            name: "Round Configs",
            path: "/admin/round-configs",
            icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
            requiredPermissions: ["roundConfigs.editAll"], // Admin hat editAll, nicht viewAll
          },
          {
            name: "Extension Settings",
            path: "/admin/site-configs",
            icon: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4",
            requiredPermissions: ["siteConfigs.viewOwn", "siteConfigs.viewAll"],
          },
          {
            name: "Scraper Configs",
            path: "/admin/scraper-configs",
            icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4",
            requiredPermissions: ["scraperConfigs.editAll"], // Kritische Funktion - nur für Admins
          },
          { type: "divider", label: "Proxys" },
          {
            name: "Proxy Management",
            path: "/admin/proxies",
            icon: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9",
            requiredPermissions: ["proxies.editAll"],
          },
          {
            name: "Browser Profiles",
            path: "/admin/browser-profiles",
            icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
            requiredPermissions: ["browserProfiles.editAll"],
          },
          {
            name: "Proxy Settings",
            path: "/admin/proxy-settings",
            icon: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4",
            requiredPermissions: ["proxySettings.editAll"],
          },
          { type: "divider", label: "Analytics" },
          {
            name: "Cost Analytics",
            path: "/admin/analytics",
            icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
            requiredPermissions: ["analytics.viewAll"],
          },
          {
            name: "Preisstatistik Settings",
            path: "/admin/price-stats-settings",
            icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
            requiredPermissions: ["analytics.viewAll"],
          },
          { type: "divider", label: "Logs" },
          {
            name: "System Logs",
            path: "/admin/logs",
            icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
            requiredPermissions: ["logs.editAll"],
          },
          {
            name: "Dashboard",
            path: "/admin/dashboard",
            icon: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z",
            requiredPermissions: ["adminDashboard.editAll"],
          },
        ],
      },
    ];

    // Filter out admin-only items if user cannot access admin features
    const filtered = items.filter(
      (item) => !item.adminOnly || authStore.canAccessAdmin,
    );

    // Filter admin children based on permissions
    return filtered.map((item) => {
      if (item.children) {
        return {
          ...item,
          children: filterMenuItems(item.children),
        };
      }
      return item;
    });
  });

  const isActive = (path: string): boolean => {
    return route.path === path;
  };

  const toggleAdmin = () => {
    isAdminExpanded.value = !isAdminExpanded.value;
  };

  return {
    navItems,
    isAdminExpanded,
    isAdminRoute,
    isActive,
    toggleAdmin,
    navigateTo,
    canSeeMenuItem,
    filterMenuItems,
  };
};
